import { Link } from 'react-router'
import PageShell from '../components/PageShell'
import { STATES } from '../content/states'
import { APP_REGISTER_URL } from '../content/constants'

const featuredGuides = [
  { to: '/guides/public-procurement', label: 'Understand public procurement' },
  { to: '/guides/how-to-bid-on-government-contracts', label: 'Learn how to prepare a bid' },
  { to: '/guides/government-bid-checklist', label: 'Use the submission checklist' },
]

/** Hub page for the six New England state acquisition pages: the public,
 * no-account entry point into the live bid inventory on the app. */
export default function GovernmentBidsPage() {
  return (
    <PageShell
      title="Government Bids Across New England"
      intro="ProcurAlerts covers public bids posted by municipal, state agency, school district, housing authority, and transit buyers across the six New England states. You can browse the current public inventory without an account, or pick a state below to see what ProcurAlerts monitors there."
    >
      {/* Public browsing comes first; registration is the secondary action. */}
      <section className="mb-12 bg-white/[0.03] border border-white/[0.06] rounded-xl p-8">
        <h2 className="text-xl font-semibold text-white mb-3">Browse the live inventory</h2>
        <p className="text-[15px] text-[#9ca3af] leading-[1.7] mb-6">
          The public bid browser lists current opportunities collected from
          official procurement sources across New England. No account is needed
          to look; a free trial adds trade- and county-matched daily digests,
          watchlists, and bid package tools.
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <a
            href="https://app.procuralerts.com/bids"
            className="inline-block text-center bg-[#06b6d4] text-[#0d1117] text-sm font-semibold px-6 py-3 rounded-lg hover:bg-[#22d3ee] transition-all duration-200"
          >
            Browse all live bids
          </a>
          <a
            href={APP_REGISTER_URL}
            className="inline-block text-center border border-white/20 text-white text-sm font-semibold px-6 py-3 rounded-lg hover:border-[#06b6d4] hover:text-[#06b6d4] transition-all duration-200"
          >
            Start your 14-day free trial
          </a>
        </div>
      </section>

      {/* State directory */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-3">Government bids by state</h2>
        <p className="text-[15px] text-[#9ca3af] leading-[1.7] mb-4">
          Each state page describes the purchasing organizations and source
          portals ProcurAlerts monitors there, and links to the state-filtered
          view of the live inventory.
        </p>
        <ul className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {STATES.map((state) => (
            <li key={state.slug}>
              <Link
                to={`/government-bids/${state.slug}`}
                className="block bg-white/[0.03] border border-white/[0.06] rounded-xl p-5 hover:border-[#06b6d4]/40 transition-colors"
              >
                <span className="text-xs font-mono uppercase tracking-[0.08em] text-[#06b6d4]">
                  {state.abbreviation}
                </span>
                <span className="mt-1 block text-base font-semibold text-white">{state.name}</span>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* Learning resources */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-3">New to public contracting?</h2>
        <p className="text-[15px] text-[#9ca3af] leading-[1.7] mb-4">
          Our <Link to="/guides" className="text-[#06b6d4] hover:text-[#22d3ee]">government contracting guides</Link>{' '}
          explain how public procurement works before you commit to a response. Start with:
        </p>
        <ul className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {featuredGuides.map((guide) => (
            <li key={guide.to}>
              <Link
                to={guide.to}
                className="block h-full bg-white/[0.03] border border-white/[0.06] rounded-xl p-5 text-sm font-semibold text-white hover:border-[#06b6d4]/40"
              >
                {guide.label}
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* Honest limitation language */}
      <aside className="border-l-2 border-[#06b6d4] bg-[#06b6d4]/[0.05] p-5 text-sm text-[#9ca3af] leading-[1.7]">
        Coverage grows over time and no service can guarantee that every posting
        from every buyer is captured. Always verify deadlines, addenda,
        requirements, documents, and submission instructions with the issuing
        organization or the official procurement source before bidding.
      </aside>
    </PageShell>
  )
}
